import React, { useState } from 'react';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth';
import { useLanguage, languages } from '../i18n';
import { CraftFoodLogo } from './brand/Logos';
import { Button } from './ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { Globe, Menu, X, ArrowRight } from 'lucide-react';
import LegalFooter from './LegalFooter';

/**
 * Public marketing layout — header, language switcher, login/register CTAs, footer.
 */
export default function PublicLayout({ children }) {
  const { isAuthenticated, user } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const currentLang = languages.find(l => l.code === language);

  const navItems = [
    { to: '/', label: t('navHome') },
    { to: '/about', label: t('navAbout') },
    { to: '/brands', label: t('navBrands') },
    { to: '/lavash', label: 'LAVASH' },
    { to: '/velora', label: 'VELORA', italic: true },
    { to: '/contacts', label: t('navContacts') },
  ];

  const goToPortal = () => {
    if (!user) return navigate('/login');
    if (user.role === 'super_admin' || user.role === 'admin') return navigate('/admin');
    if (user.role === 'operations_manager') return navigate('/operations');
    return navigate('/client');
  };

  return (
    <div className="min-h-screen bg-[#F6F4EE] text-[#1A1A1A] flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#F6F4EE]/85 backdrop-blur-md border-b border-[#1A1A1A]/8" data-testid="public-header">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <CraftFoodLogo size="sm" />

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-8">
            {navItems.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                data-testid={`pub-nav-${item.to.replace('/', '') || 'home'}`}
                className={({ isActive }) => `text-sm tracking-wide font-medium transition-colors ${
                  isActive ? 'text-[#1A1A1A]' : 'text-[#1A1A1A]/55 hover:text-[#1A1A1A]'
                } ${item.italic ? 'italic font-playfair font-bold' : ''}`}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-1.5 text-[#1A1A1A]" data-testid="public-lang">
                  <Globe className="h-4 w-4" />
                  {currentLang?.flag}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {languages.map(lang => (
                  <DropdownMenuItem key={lang.code} onClick={() => setLanguage(lang.code)} data-testid={`public-lang-${lang.code}`}>
                    <span className="mr-2">{lang.flag}</span> {lang.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {isAuthenticated ? (
              <Button onClick={goToPortal} className="cff-button-primary px-5" data-testid="header-portal">
                {t('myPortal')} <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <>
                <Button variant="ghost" onClick={() => navigate('/login')} className="text-[#1A1A1A]" data-testid="header-login">
                  {t('login')}
                </Button>
                <Button onClick={() => navigate('/register')} className="cff-button-primary px-5" data-testid="header-register">
                  {t('register')} <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </>
            )}
          </div>

          {/* Mobile menu trigger */}
          <button
            className="lg:hidden p-2"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-toggle"
            aria-label="menu"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="lg:hidden border-t border-[#1A1A1A]/10 bg-[#F6F4EE]" data-testid="mobile-menu">
            <div className="px-6 py-6 space-y-4">
              {navItems.map(item => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  onClick={() => setMobileOpen(false)}
                  data-testid={`pub-mnav-${item.to.replace('/', '') || 'home'}`}
                  className={({ isActive }) => `block text-base font-medium ${
                    isActive ? 'text-[#1A1A1A]' : 'text-[#1A1A1A]/60'
                  } ${item.italic ? 'italic font-playfair' : ''}`}
                >
                  {item.label}
                </NavLink>
              ))}
              <div className="pt-4 border-t border-[#1A1A1A]/10 flex flex-wrap gap-2">
                {languages.map(lang => (
                  <button
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    className={`text-xs px-3 py-1.5 rounded-sm ${language === lang.code ? 'bg-[#1A1A1A] text-[#F6F4EE]' : 'border border-[#1A1A1A]/20 text-[#1A1A1A]/70'}`}
                  >
                    {lang.flag} {lang.name}
                  </button>
                ))}
              </div>
              <div className="flex flex-col gap-2 pt-3">
                {isAuthenticated ? (
                  <Button onClick={goToPortal} className="cff-button-primary w-full">{t('myPortal')}</Button>
                ) : (
                  <>
                    <Button variant="outline" onClick={() => navigate('/login')} className="w-full cff-button-outline">{t('login')}</Button>
                    <Button onClick={() => navigate('/register')} className="cff-button-primary w-full">{t('register')}</Button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Page content */}
      <main className="flex-1">{children}</main>

      {/* Legal footer (required by Korean PG) */}
      <LegalFooter dark />
    </div>
  );
}
